import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ServiceItem } from "../../../mocks/services";

interface AdminServiceListProps {
  services: ServiceItem[];
  onEdit: (item: ServiceItem) => void;
  onDelete: (id: string) => void;
}

export default function AdminServiceList({ services, onEdit, onDelete }: AdminServiceListProps) {
  const { t } = useTranslation("admin");
  const [confirmId, setConfirmId] = useState<string | null>(null);

  if (services.length === 0) {
    return (
      <div
        style={{
          padding: "48px 32px",
          textAlign: "center",
          borderRadius: 16,
          border: "1.5px dashed rgba(44,95,79,0.2)",
          background: "rgba(44,95,79,0.02)",
        }}
      >
        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4"
          style={{ borderRadius: "50%", background: "rgba(44,95,79,0.08)" }}>
          <i className="ri-stack-line" style={{ fontSize: 20, color: "#2C5F4F" }} />
        </div>
        <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: 14, color: "#888" }}>
          {t("list.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {services.map((svc) => (
        <div
          key={svc.id}
          style={{
            background: "#ffffff",
            borderRadius: 14,
            border: "1px solid rgba(44,95,79,0.1)",
            overflow: "hidden",
          }}
        >
          <div className="flex items-center gap-4 p-4">
            <div
              style={{
                width: 72,
                height: 56,
                borderRadius: 10,
                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <img
                src={svc.imageUrl}
                alt={svc.name}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#1A1A1A",
                  marginBottom: 3,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {svc.name}
              </p>
              <p
                style={{
                  fontFamily: "'Noto Serif JP', serif",
                  fontSize: 12,
                  color: "#888888",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {svc.description}
              </p>
              {svc.link && (
                <p
                  style={{
                    fontFamily: "'Outfit', monospace",
                    fontSize: 11,
                    color: "#2C5F4F",
                    marginTop: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <i className="ri-link" style={{ marginRight: 4 }} />
                  {svc.link}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => onEdit(svc)}
                className="cursor-pointer flex items-center justify-center w-8 h-8 whitespace-nowrap"
                style={{
                  borderRadius: 8,
                  border: "1.5px solid rgba(44,95,79,0.2)",
                  background: "transparent",
                  color: "#2C5F4F",
                  transition: "background 0.15s",
                }}
                title={t("list.edit")}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.background = "rgba(44,95,79,0.06)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.background = "transparent")
                }
              >
                <i className="ri-edit-line" style={{ fontSize: 15 }} />
              </button>
              <button
                type="button"
                onClick={() => setConfirmId(svc.id)}
                className="cursor-pointer flex items-center justify-center w-8 h-8 whitespace-nowrap"
                style={{
                  borderRadius: 8,
                  border: "1.5px solid rgba(220,38,38,0.2)",
                  background: "transparent",
                  color: "#dc2626",
                  transition: "background 0.15s",
                }}
                title={t("list.delete")}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.background = "rgba(220,38,38,0.05)")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLButtonElement).style.background = "transparent")
                }
              >
                <i className="ri-delete-bin-line" style={{ fontSize: 15 }} />
              </button>
            </div>
          </div>

          {/* Confirm delete inline */}
          {confirmId === svc.id && (
            <div
              style={{
                borderTop: "1px solid rgba(220,38,38,0.1)",
                background: "rgba(220,38,38,0.03)",
                padding: "12px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <p
                style={{
                  fontFamily: "'Noto Sans JP', sans-serif",
                  fontSize: 13,
                  color: "#dc2626",
                }}
              >
                {t("list.confirmDelete", { name: svc.name })}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmId(null)}
                  className="whitespace-nowrap cursor-pointer"
                  style={{
                    padding: "6px 14px",
                    borderRadius: 8,
                    border: "1.5px solid #cccccc",
                    background: "transparent",
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#555",
                  }}
                >
                  {t("list.cancel")}
                </button>
                <button
                  type="button"
                  onClick={() => { onDelete(svc.id); setConfirmId(null); }}
                  className="whitespace-nowrap cursor-pointer"
                  style={{
                    padding: "6px 14px",
                    borderRadius: 8,
                    border: "none",
                    background: "#dc2626",
                    fontFamily: "'Noto Sans JP', sans-serif",
                    fontSize: 12,
                    fontWeight: 700,
                    color: "#ffffff",
                  }}
                >
                  {t("list.confirmDeleteButton")}
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
